import { Form, FormikProps, useFormikContext } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  ImageFileFormats,
  MimeEnum,
  StaticImageFileFormats,
  VideoFileFormats,
  WavFileFormats,
} from "../../../common/commonEnums";
import Button from "../../../common/components/Buttons/Button";
import {
  FormControl,
  FormError as Error,
  FormInput,
  FormTextArea,
  FormSwitch,
} from "../../../common/components/FormControls";
import FormSelect from "../../../common/components/FormControls/FormSelect";
import { FormDescription, FormLabel } from "../../../common/components/styled";
import { countries } from "../../../common/countries";
import { genresOptions } from "../../../common/genres";
import { languages } from "../../../common/languages";
import { Heading6Bold } from "../../../common/Typography/Heading6";
import { getHumanReadableFileSize } from "../../../helpers/fileHelper";
import MusicPreview from "./MusicPreview";
import FileUpload, { FileUploadType } from "./FileUpload";
import { MusicFormValues } from "./UploadMusic";
import { useUploadMusicContext } from "./useUploadMusic";
import { settings } from "../../../settings";
import { useMusicPreview } from "./useMusicPreview";
import FadeImage from "../../../common/components/FadeImage/FadeImage";
import { getMediumTypography } from "../../../common/Typography/helpers";
import { getHorizontalGap } from "../../../common/styles";
import ButtonOutlined from "../../../common/components/Buttons/ButtonOutlined";
import FullPreviewModal from "./FullPreviewModal";
import ScrollToError from "../../../common/components/FormControls/ScrollToError";
import {
  TRACK_COVER_ART_SIZES,
  TRACK_POSTER_ART_SIZES,
} from "../../../constants/imageSizes";
import { useApiValidation } from "../../../common/components/FormControls/useApiValidation";
import { validateTrackName } from "../../../helpers/validationHelper";
import { useSession } from "../../../queries/authentication.query";

const Content = styled.div`
  display: flex;

  @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
    flex-direction: column;
  }
`;

const FormError = styled(Error)`
  div {
    color: ${({ theme }) => theme.errorColor};
    opacity: 1;
  }
`;

const FormContainer = styled.div`
  width: 600px;
  @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
    width: 100%;
  }
`;

const PreviewContainer = styled.div`
  position: sticky;
  top: 90px;
  margin-left: 109px;
  width: 400px;
  max-width: 400px;
  height: fit-content;

  display: flex;
  flex-direction: column;
  flex-grow: 1;

  button.upload {
    width: fit-content;
    align-self: flex-end;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
    width: 100%;
    margin: 0 auto;
    margin-top: 24px;
    max-width: 650px;
    position: relative;
    top: 0;
  }
`;

const SectionHeading = styled(Heading6Bold)`
  margin-bottom: 2px;
`;

const PreviewHeading = styled(SectionHeading)`
  margin-bottom: 6px;
`;

const Master = styled.div``;
const CoverArt = styled.div``;
const Poster = styled.div``;
const PosterPreview = styled.div`
  position: relative;
  margin: 25px 0;
  height: 200px;
  width: 100%;
  border-radius: ${(props) => props.theme.borderRadius.md};
  overflow: hidden;
`;
const FieldLabels = styled.div`
  display: flex;
  justify-content: space-between;
`;
const MarginedSection = styled.div`
  margin-bottom: 25px;
`;
const Instrumental = styled.div``;
const Vocals = styled.div``;
const Video = styled.div``;

const TrackDetails = styled.div`
  padding-bottom: 25px;
  margin-bottom: 25px;
  border-bottom: 2px solid ${(props) => props.theme.borderColor};
`;

const TrackDetailsTitle = styled(SectionHeading)`
  margin-bottom: 16px;
`;

const FormLabelStyled = styled(FormLabel)`
  ${getMediumTypography};
  line-height: 14px;
  letter-spacing: 0.1em;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  ${getHorizontalGap("10px")};
`;

interface UploadMusicFormProps {
  isDisabled?: boolean;
  isLoading?: boolean;
  onUploadStart: () => void;
  onUploadEnd: () => void;
}

const UploadMusicForm = ({
  isLoading,
  isDisabled,
  onUploadStart,
  onUploadEnd,
}: UploadMusicFormProps & FormikProps<MusicFormValues>) => {
  const { t } = useTranslation();
  const { user } = useSession();
  const { errors, touched, submitForm, values, isValid } =
    useFormikContext<MusicFormValues>();
  const validateTrackNameCached = useApiValidation(validateTrackName);
  const {
    setMaster,
    setFull,
    setInstrumental,
    setVocal,
    setCover,
    setPoster,
    setVideo,
  } = useUploadMusicContext();
  const musicPreview = useMusicPreview();
  const [isPreviewOpened, setIsPreviewOpened] = useState(false);
  const togglePreview = () => setIsPreviewOpened((value) => !value);

  return (
    <Form noValidate>
      <ScrollToError />
      <Content>
        <FormContainer>
          <Master>
            <SectionHeading>{t("upload-music-full-track")}</SectionHeading>
            <FormDescription>
              {t("upload-music-full-track-description")}
            </FormDescription>
            <FileUpload
              description={t("upload-music-mastered-wav")}
              fileFormats={WavFileFormats}
              label="WAV"
              name="masterWav"
              onFileUploaded={setMaster}
              onFileDeleted={() => setMaster(undefined)}
              onUploadStart={onUploadStart}
              onUploadEnd={onUploadEnd}
              type={FileUploadType.AUDIO}
              errorMessageOptions={{
                size: getHumanReadableFileSize(settings.MAX_AUDIO_SIZE),
              }}
            />
            <FileUpload
              description={t("upload-music-mastered-mp3")}
              fileFormats={[MimeEnum.mp3]}
              label="MP3"
              name="masterMp3"
              onFileUploaded={setFull}
              onFileDeleted={() => setFull(undefined)}
              onUploadStart={onUploadStart}
              onUploadEnd={onUploadEnd}
              type={FileUploadType.AUDIO}
              errorMessageOptions={{
                size: getHumanReadableFileSize(settings.MAX_AUDIO_SIZE),
              }}
            />
          </Master>
          <CoverArt>
            <div>
              <SectionHeading>{t("upload-music-cover-art")}</SectionHeading>
              <FormDescription>
                {t("upload-music-required-cover-dimensions", {
                  size: settings.COVER_WIDTH,
                })}
              </FormDescription>
            </div>
            <FileUpload
              fileFormats={[...ImageFileFormats, MimeEnum.mp4]}
              label="IMAGE"
              name="cover"
              useCloudinaryVideo
              onFileUploaded={setCover}
              onFileDeleted={() => setCover(undefined)}
              onUploadStart={onUploadStart}
              onUploadEnd={onUploadEnd}
              type={FileUploadType.IMAGE}
              errorMessageOptions={{
                width: settings.COVER_WIDTH,
                height: settings.COVER_WIDTH,
                size: getHumanReadableFileSize(settings.MAX_IMAGE_SIZE),
              }}
              thumbnailSizes={TRACK_COVER_ART_SIZES}
            />
          </CoverArt>
          <TrackDetails>
            <TrackDetailsTitle>
              {t("upload-music-track-details")}
            </TrackDetailsTitle>
            <MarginedSection>
              <FormControl>
                <FormLabelStyled htmlFor="name">
                  {t("upload-music-track-name")}
                </FormLabelStyled>
                <FormDescription>
                  {t("upload-music-track-name-desc")}
                </FormDescription>
                <FormInput
                  name="name"
                  type="text"
                  validate={(value: string | undefined) =>
                    validateTrackNameCached(value, user!.id)
                  }
                />
                <FormError error={t(errors.name!)} touched={touched.name} />
              </FormControl>
            </MarginedSection>
            <MarginedSection>
              <FormControl>
                <FormLabelStyled htmlFor="countries">
                  {t("upload-music-countries")}
                </FormLabelStyled>
                <FormSelect
                  name="countries"
                  id="countries"
                  options={countries.map((country) => ({
                    label: country.name,
                    value: country.code,
                  }))}
                  placeholder={t("select-countries")}
                  /* @ts-ignore */
                  isMulti
                />
                <FormError
                  error={t(errors.countries!)}
                  touched={touched.countries}
                />
              </FormControl>
            </MarginedSection>

            <MarginedSection>
              <FormControl>
                <FieldLabels>
                  <FormLabelStyled htmlFor="description">
                    {t("upload-music-description")}
                  </FormLabelStyled>
                </FieldLabels>
                <FormDescription>
                  {t("upload-music-description-desc")}
                </FormDescription>
                <FormTextArea
                  forwardedAs="textarea"
                  name="description"
                  type="text"
                  rows="5"
                />
                <FormError
                  error={t(errors.description!)}
                  touched={touched.description}
                />
              </FormControl>
            </MarginedSection>
            <FormControl>
              <FormLabelStyled htmlFor="hidden">
                {t("upload-music-hide-track")}
              </FormLabelStyled>
              <FormDescription>{t("hide-instruction")}</FormDescription>
              <FormSwitch name="hidden" />
            </FormControl>
          </TrackDetails>
          {/* optional section */}
          <Poster>
            <div>
              <SectionHeading>{t("upload-music-poster")}</SectionHeading>
              <FieldLabels>
                <FormDescription>
                  {t("upload-music-required-poster-dimensions", {
                    width: settings.TRACK_POSTER.WIDTH,
                    height: settings.TRACK_POSTER.HEIGHT,
                  })}
                </FormDescription>
                <FormDescription>{t("upload-music-optional")}</FormDescription>
              </FieldLabels>
            </div>
            <FileUpload
              fileFormats={StaticImageFileFormats}
              label="IMAGE"
              name="poster"
              onFileUploaded={setPoster}
              onFileDeleted={() => setPoster(undefined)}
              onUploadStart={onUploadStart}
              onUploadEnd={onUploadEnd}
              type={FileUploadType.IMAGE}
              errorMessageOptions={{
                width: settings.TRACK_POSTER.WIDTH,
                height: settings.TRACK_POSTER.HEIGHT,
                size: getHumanReadableFileSize(settings.MAX_IMAGE_SIZE),
              }}
              thumbnailSizes={TRACK_POSTER_ART_SIZES}
            />
          </Poster>
          {musicPreview?.poster?.url && !errors.poster && (
            <PosterPreview>
              <FadeImage src={musicPreview.poster.url} />
            </PosterPreview>
          )}
          <MarginedSection>
            <FormControl>
              <FieldLabels>
                <FormLabelStyled htmlFor="genre">
                  {t("upload-music-genres")}
                </FormLabelStyled>
                <FormDescription>{t("upload-music-optional")}</FormDescription>
              </FieldLabels>
              <FormDescription>{t("upload-music-genres-desc")}</FormDescription>
              <FormSelect
                name="genres"
                options={
                  // limit to max 2 selections
                  (values.genres?.length ?? 0) >= 2
                    ? genresOptions.filter((option) =>
                        values.genres?.includes(option.value)
                      )
                    : genresOptions
                }
                placeholder={t("select-genres")}
                /* @ts-ignore */
                isMulti
                noOptionsMessage={() => t("upload-music-genres-max-info")}
              />
            </FormControl>
          </MarginedSection>
          <MarginedSection>
            <FormControl>
              <FieldLabels>
                <FormLabelStyled htmlFor="language">
                  {t("upload-music-languages")}
                </FormLabelStyled>
                <FormDescription>{t("upload-music-optional")}</FormDescription>
              </FieldLabels>
              <FormDescription>
                {t("upload-music-languages-desc")}
              </FormDescription>
              <FormSelect
                name="languages"
                options={languages.map((lang) => ({
                  label: lang.name,
                  value: lang.code,
                }))}
                placeholder={t("select-languages")}
                /* @ts-ignore */
                isMulti
              />
            </FormControl>
          </MarginedSection>

          <Instrumental>
            <FieldLabels>
              <SectionHeading>
                {t("upload-music-full-instrumental")}
              </SectionHeading>
              <FormDescription>{t("upload-music-optional")}</FormDescription>
            </FieldLabels>
            <FileUpload
              fileFormats={WavFileFormats}
              name="instrumental"
              onFileUploaded={setInstrumental}
              onFileDeleted={() => setInstrumental(undefined)}
              onUploadStart={onUploadStart}
              onUploadEnd={onUploadEnd}
              type={FileUploadType.AUDIO}
              label="WAV"
              errorMessageOptions={{
                size: getHumanReadableFileSize(settings.MAX_AUDIO_SIZE),
              }}
            />
          </Instrumental>
          <Vocals>
            <FieldLabels>
              <SectionHeading>{t("upload-music-full-vocals")}</SectionHeading>
              <FormDescription>{t("upload-music-optional")}</FormDescription>
            </FieldLabels>
            <FileUpload
              fileFormats={WavFileFormats}
              name="vocals"
              onFileUploaded={setVocal}
              onFileDeleted={() => setVocal(undefined)}
              onUploadStart={onUploadStart}
              onUploadEnd={onUploadEnd}
              type={FileUploadType.AUDIO}
              label="WAV"
              errorMessageOptions={{
                size: getHumanReadableFileSize(settings.MAX_AUDIO_SIZE),
              }}
            />
          </Vocals>
          <Video>
            <FieldLabels>
              <SectionHeading>{t("upload-music-video")}</SectionHeading>
              <FormDescription>{t("upload-music-optional")}</FormDescription>
            </FieldLabels>
            <FileUpload
              fileFormats={VideoFileFormats}
              name="video"
              onFileUploaded={setVideo}
              onFileDeleted={() => setVideo(undefined)}
              onUploadStart={onUploadStart}
              onUploadEnd={onUploadEnd}
              type={FileUploadType.AUDIO}
              label="MP4"
              errorMessageOptions={{
                size: getHumanReadableFileSize(settings.MAX_VIDEO_SIZE),
              }}
            />
          </Video>
        </FormContainer>
        <PreviewContainer>
          <PreviewHeading>{t("upload-music-preview")}</PreviewHeading>
          <MusicPreview music={musicPreview} />
          <ButtonsContainer>
            <ButtonOutlined
              type="button"
              text={t("upload-music-full-preview")}
              onClick={togglePreview}
              disabled={!isValid}
            />
            <Button
              text={t("upload-music-button-text")}
              className="upload"
              disabled={isDisabled}
              isLoading={isLoading}
              onClick={(e: any) => {
                e.preventDefault();
                submitForm();
              }}
            />
          </ButtonsContainer>
        </PreviewContainer>
      </Content>
      <FullPreviewModal
        isOpen={isPreviewOpened}
        onClose={togglePreview}
        music={musicPreview}
      />
    </Form>
  );
};

export default UploadMusicForm;
